import { useState } from 'react';
import {
  IonContent,
  IonPage,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonBackButton,
  IonSpinner,
  IonText,
  IonIcon,
  useIonRouter,
} from '@ionic/react';
import { flag } from 'ionicons/icons';
import { useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client/react';
import { GET_POST } from '../../../../graphql/queries/post';
import {
  UserContentTypeEnum,
  type GetPostQuery,
  type GetPostQueryVariables,
} from '../../../../types/graphql-generated';
import { FeatureFlag } from '../../../../types/featureFlags';
import { useDeletePost } from '../../../../hooks/useDeletePost';
import { useFeatureFlags } from '../../../../hooks/useFeatureFlags';
import { useUserAccount } from '../../../../hooks/useUserAccount';
import { AppSectionHeader } from '../../../components/ui/AppSectionHeader';
import { MarkdownContent } from '../../../../components/markdown/MarkdownContent';
import { PostMeta } from './PostMeta';
import { PostLinkAttachment } from './PostLinkAttachment';
import { PostPhotoGrid } from './photos/PostPhotoGrid';
import { PostActionsMenu } from './PostActionsMenu';
import { DeleteConfirmAlert } from './DeleteConfirmAlert';
import { ChannelImage } from './ChannelImage';
import { ReportContentModal } from '../../../components/ReportContentModal';

// Full-post detail screen reached by tapping a timeline card. Fetches the post
// by id so it works on reload / deep-link. Like the timeline card, it shows the
// channel's image and only names the author when the channel publishes it.
export const PostDetailPage: React.FC = () => {
  const postId = useParams<'postId'>().postId!;
  const ionRouter = useIonRouter();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const { isEnabled } = useFeatureFlags();
  const { userAccount } = useUserAccount();

  const { data, loading } = useQuery<GetPostQuery, GetPostQueryVariables>(GET_POST, {
    variables: { id: postId },
  });

  const { confirmDelete } = useDeletePost({
    successMessage: 'Publicación eliminada',
    errorMessage: 'No se pudo eliminar la publicación',
  });

  const post = data?.post;
  const canReportPost =
    isEnabled(FeatureFlag.REPORT_USER_CONTENT) &&
    !!post?.author &&
    !!userAccount?.id &&
    post.author.id !== userAccount.id;

  return (
    <IonPage>
      <AppSectionHeader variant="main">
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/app/main/channels" />
          </IonButtons>
          <IonTitle className="ml-4">Publicación</IonTitle>
        </IonToolbar>
      </AppSectionHeader>
      <IonContent className="ion-padding">
        {loading && (
          <div className="flex justify-center items-center min-h-[200px]">
            <IonSpinner name="crescent" />
          </div>
        )}

        {!loading && !post && (
          <div className="flex justify-center items-center min-h-[200px]">
            <IonText color="medium">
              <p>No se ha encontrado la publicación</p>
            </IonText>
          </div>
        )}

        {post && (
          <div>
            <div className="flex items-center justify-between gap-2">
              <div className="flex min-w-0 items-center gap-3">
                <ChannelImage
                  image={post.channel?.image}
                  updatedAt={post.channel?.updatedAt}
                  name={post.channel?.name}
                  size={44}
                />
                <PostMeta
                  channelName={post.channel?.name ?? ''}
                  channelId={post.channel?.id}
                  authorName={post.channel?.publishAuthorName ? post.author?.fullName : null}
                  publishedAt={post.publishedAt}
                  editedAt={post.editedAt}
                  size="lg"
                />
              </div>
              {post.channel?.viewerIsOwner && post.channel?.id && (
                <PostActionsMenu
                  canEdit={!!post.editable}
                  onEdit={() =>
                    ionRouter.push(`/app/main/channels/${post.channel?.id}/publicar/${post.id}`)
                  }
                  onDelete={() => setShowDeleteConfirm(true)}
                />
              )}
            </div>
            <MarkdownContent
              content={post.text}
              className="mt-4 text-gray-700 dark:text-gray-300"
            />
            <PostLinkAttachment url={post.linkUrl} title={post.linkTitle} />
            <PostPhotoGrid photos={post.photos} />

            {canReportPost && (
              <div className="mt-3 flex justify-end">
                <button
                  type="button"
                  aria-label="Reportar publicación"
                  onClick={() => setShowReportModal(true)}
                  className="inline-flex items-center text-red-600 transition-colors hover:text-red-700"
                >
                  <IonIcon icon={flag} className="text-base" />
                </button>
              </div>
            )}

            <ReportContentModal
              isOpen={showReportModal}
              onClose={() => setShowReportModal(false)}
              contentType={UserContentTypeEnum.Post}
              contentId={post.id}
            />

            <DeleteConfirmAlert
              isOpen={showDeleteConfirm}
              header="Eliminar publicación"
              message="¿Seguro que quieres eliminar esta publicación? Esta acción no se puede deshacer."
              onDidDismiss={() => setShowDeleteConfirm(false)}
              onConfirm={() => {
                if (post.channel?.id) {
                  void confirmDelete({ channelId: post.channel.id, postId: post.id });
                }
              }}
            />
          </div>
        )}
      </IonContent>
    </IonPage>
  );
};
